import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

// Mock data
const mockBids = [
  {
    id: 1,
    itemName: 'Equus Dualis II #05',
    collection: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 5.png',
    myBid: 68.5,
    currentBid: 68.5,
    currency: 'ETH',
    endTime: new Date(Date.now() + 3600000 * 5),
    status: 'winning',
    auctionType: 'English',
    bidTime: new Date(Date.now() - 3600000 * 2),
  },
  {
    id: 2,
    itemName: 'Warrior Mythology #17',
    collection: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology17.jpg',
    myBid: 40,
    currentBid: 45,
    currency: 'ETH',
    endTime: new Date(Date.now() + 3600000 * 12),
    status: 'outbid',
    auctionType: 'English',
    bidTime: new Date(Date.now() - 3600000 * 8),
  },
  {
    id: 3,
    itemName: 'Equus Dualis II #18',
    collection: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 18.png',
    myBid: 12.5,
    currentBid: 12.5,
    currency: 'ETH',
    endTime: new Date(Date.now() - 3600000 * 24),
    status: 'won',
    auctionType: 'English',
    bidTime: new Date(Date.now() - 3600000 * 48),
  },
  {
    id: 4,
    itemName: 'Warrior Mythology #43',
    collection: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology43.jpg',
    myBid: 10,
    currentBid: 15,
    currency: 'ETH',
    endTime: new Date(Date.now() - 3600000 * 12),
    status: 'lost',
    auctionType: 'English',
    bidTime: new Date(Date.now() - 3600000 * 36),
  },
];

const BidsPage: React.FC = () => {
  const [filter, setFilter] = useState('all');
  const [bids] = useState(mockBids);

  const formatTimeRemaining = (endTime: Date) => {
    const now = new Date();
    const diff = endTime.getTime() - now.getTime();

    if (diff <= 0) return 'Finalizada';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'winning':
        return { label: 'Ganando', color: 'bg-green-500/20 text-green-400', icon: ArrowTrendingUpIcon };
      case 'outbid':
        return { label: 'Superado', color: 'bg-red-500/20 text-red-400', icon: ArrowTrendingUpIcon };
      case 'won':
        return { label: 'Ganada', color: 'bg-primary-500/20 text-primary-400', icon: CheckCircleIcon };
      case 'lost':
        return { label: 'Perdida', color: 'bg-gray-500/20 text-gray-400', icon: XCircleIcon };
      default:
        return { label: 'Pendiente', color: 'bg-gray-500/20 text-gray-400', icon: ClockIcon };
    }
  };

  const filteredBids = filter === 'all'
    ? bids
    : bids.filter(bid => {
        if (filter === 'active') return bid.status === 'winning' || bid.status === 'outbid';
        if (filter === 'won') return bid.status === 'won';
        if (filter === 'lost') return bid.status === 'lost';
        return true;
      });

  const activeBids = bids.filter(b => b.status === 'winning' || b.status === 'outbid');
  const wonBids = bids.filter(b => b.status === 'won');
  const lostBids = bids.filter(b => b.status === 'lost');

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <ChartBarIcon className="w-8 h-8 text-blue-400" />
            Mis Pujas
          </h1>
          <p className="text-gray-400">Historial de todas tus pujas</p>
        </div>

        {/* Stats */}
        <div className="flex gap-4">
          <div className="bg-dark-800/50 rounded-lg px-4 py-2 border border-dark-700">
            <p className="text-xs text-gray-500">Activas</p>
            <p className="text-xl font-bold text-white">{activeBids.length}</p>
          </div>
          <div className="bg-dark-800/50 rounded-lg px-4 py-2 border border-dark-700">
            <p className="text-xs text-gray-500">Ganadas</p>
            <p className="text-xl font-bold text-green-400">{wonBids.length}</p>
          </div>
          <div className="bg-dark-800/50 rounded-lg px-4 py-2 border border-dark-700">
            <p className="text-xs text-gray-500">Perdidas</p>
            <p className="text-xl font-bold text-gray-400">{lostBids.length}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { key: 'all', label: 'Todas' },
          { key: 'active', label: 'Activas' },
          { key: 'won', label: 'Ganadas' },
          { key: 'lost', label: 'Perdidas' },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => setFilter(item.key)}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap ${
              filter === item.key
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-gray-400 hover:text-white'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Bids List */}
      {filteredBids.length > 0 ? (
        <div className="space-y-4">
          {filteredBids.map((bid, idx) => {
            const statusInfo = getStatusInfo(bid.status);
            const StatusIcon = statusInfo.icon;
            const isActive = bid.status === 'winning' || bid.status === 'outbid';

            return (
              <motion.div
                key={bid.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-dark-800/50 rounded-xl border border-dark-700 p-4 hover:border-primary-500/30 transition-all"
              >
                <div className="flex items-center gap-4">
                  {/* Image */}
                  <Link to={`/auction/${bid.id}`} className="flex-shrink-0">
                    <img
                      src={bid.image}
                      alt={bid.itemName}
                      className="w-20 h-20 rounded-xl object-cover hover:scale-105 transition-transform"
                    />
                  </Link>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-500">{bid.collection}</span>
                      <span className="px-2 py-0.5 bg-dark-700 text-gray-400 text-xs rounded">
                        {bid.auctionType}
                      </span>
                    </div>
                    <Link to={`/auction/${bid.id}`}>
                      <h3 className="text-lg font-semibold text-white hover:text-primary-400 transition-colors">
                        {bid.itemName}
                      </h3>
                    </Link>
                    <p className="text-xs text-gray-500 mt-1">
                      Puja realizada: {formatDate(bid.bidTime)}
                    </p>
                  </div>

                  {/* Bid Info */}
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Mi Puja</p>
                    <p className="text-xl font-bold text-white">{bid.myBid} {bid.currency}</p>
                    {bid.myBid !== bid.currentBid && (
                      <p className="text-xs text-gray-500">
                        Actual: {bid.currentBid} {bid.currency}
                      </p>
                    )}
                  </div>

                  {/* Status & Actions */}
                  <div className="flex flex-col items-end gap-2">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${statusInfo.color}`}>
                      <StatusIcon className="w-4 h-4" />
                      <span className="font-medium text-sm">{statusInfo.label}</span>
                    </div>

                    {isActive && (
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <ClockIcon className="w-4 h-4" />
                        {formatTimeRemaining(bid.endTime)}
                      </div>
                    )}

                    <div className="flex gap-2 mt-2">
                      {bid.status === 'outbid' && (
                        <Link
                          to={`/auction/${bid.id}`}
                          className="px-4 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors text-sm"
                        >
                          Superar Puja
                        </Link>
                      )}
                      {bid.status === 'won' && (
                        <Link
                          to="/dashboard/purchases"
                          className="px-4 py-1.5 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-colors text-sm"
                        >
                          Ver Compra
                        </Link>
                      )}
                      <Link
                        to={`/auction/${bid.id}`}
                        className="px-4 py-1.5 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors text-sm"
                      >
                        Ver Subasta
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <ChartBarIcon className="w-16 h-16 text-gray-700 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            No hay pujas en esta categoría
          </h3>
          <p className="text-gray-400 mb-6">
            {filter === 'all'
              ? 'Aún no has realizado ninguna puja'
              : `No tienes pujas ${filter === 'active' ? 'activas' : filter === 'won' ? 'ganadas' : 'perdidas'}`}
          </p>
          <Link
            to="/explore"
            className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            Explorar Subastas
          </Link>
        </div>
      )}
    </div>
  );
};

export default BidsPage;
